import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { defer, EMPTY } from 'rxjs';
import { catchError, filter, skip } from 'rxjs/operators';
import { clearAllState, clearDedupApiCache } from '../state-files/action';
import { DashboardService } from './dashboard.service';
import { TawkService } from './tawk.service';
import { UserService } from './user.service';
import { ReopeningsFiltersStateManagementService } from './reopenings-filters-state-management.service';
import { ManagedTabService } from './managed-tab.service';
import { AuthSessionWatchdogService } from './auth-session-watchdog.service';
import { SessionPolicyService } from './session-policy.service';
import { SessionIdleWarningService } from './session-idle-warning.service';
import { LOCAL_ACTIVITY_BROADCAST_INTERVAL_MS, SessionActivityService } from './session-activity.service';

/**
 * Largest delay a JS timer honours (2^31 - 1 ms, about 24.8 days). A larger value overflows and the timer fires
 * IMMEDIATELY, which would log a user out the moment a long (up to 720 h) absolute deadline is armed (N3). Every deadline
 * timer is clamped to this; when it fires early the callback re-evaluates the deadlines and simply re-arms.
 */
export const MAX_TIMER_DELAY_MS = 2_147_483_647;

/**
 * N1: before an IDLE logout runs, wait this long and re-check the shared estimate, so activity of a sibling tab that is
 * still in flight (its trailing broadcast is sent at most {@link LOCAL_ACTIVITY_BROADCAST_INTERVAL_MS} after the
 * activity) can still keep this tab signed in. Equal to the broadcast throttle window. Never applied to the absolute expiry.
 */
export const IDLE_LOGOUT_GRACE_MS = LOCAL_ACTIVITY_BROADCAST_INTERVAL_MS;

/**
* Service that monitors user activity and automatically logs out and clears application state after a period of inactivity (20 minutes).
* @component
* @selector app-authentication-service
* @example
*   <app-authentication-service></app-authentication-service>
*   // Typical usage is via DI in a component or module:
*   // constructor(private authService: AuthenticationService) {}
* @input none none - This injectable service has no input properties; it is configured via DI and constants within the service.
* @output none none - This service does not emit DOM outputs. It performs side effects (dispatches store actions, clears session storage, and navigates) on timeout.
*
* Purpose:
*   - Watches global user interactions (mousemove, keydown, mousedown, touchstart) and resets an inactivity timer on each interaction.
*   - If no activity is detected for the configured idle time (20 minutes), it automatically logs the user out.
*
* Key properties:
*   - idleTime (readonly, number): The inactivity threshold in milliseconds (20 * 60 * 1000).
*   - timeout (private, any): Handle for the inactivity timeout so it can be reset or cleared.
*
* Key methods:
*   - constructor(router: Router, ngZone: NgZone, store: Store, userService: UserService, tawkService: TawkService, dashboardService: DashboardService):
*       Initializes the service and starts watching for user activity (singleton at root scope). userService, tawkService, and dashboardService are injected so the inactivity-logout teardown can reset auth state, unload the chat visitor session, and revoke the session server-side.
*   - private startWatching(): Attaches global event listeners outside Angular's zone to avoid unnecessary change detection; on activity it re-arms the timers (local-default mode only; with a server policy UI input is ignored).
*   - private arm(): Clears the timers and re-schedules the warning and idle logout from the server-activity estimate (server policy) or the last UI input (local default).
*   - private deadlines(): Computes the warning and logout instants.
*   - private logout(): Runs inside Angular's zone to perform the full inactivity-logout teardown: dispatches clearAllState() and clearDedupApiCache() to the store, resets UserService auth state (token, refresh observable, vaulted credentials), unloads the Tawk chat visitor, clears localStorage and sessionStorage, and navigates to the '/emailVerification' route.
*
* Important lifecycle considerations:
*   - ProvidedIn: 'root' makes the service a singleton; it begins watching immediately in the constructor, so ensure it is only instantiated when intended (typically at application start).
*   - Uses NgZone.runOutsideAngular for event listeners to prevent frequent change detection cycles and NgZone.run to re-enter Angular when performing navigation or store updates.
*   - Event listeners are added to window; if the application mounts/unmounts or during testing, consider tearing down or mocking as needed.
*/
@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private timeout: any;
  private warningTimeout: any;
  /** Last UI input; used only while no server idle policy is in force. */
  private localActivityAt = Date.now();

  /**
   * Injects the session collaborators and starts the idle watch immediately.
   *
   * Watching begins here rather than on demand because the service is
   * root-scoped and must be counting from the moment the app boots.
   * @param router - Router used to send the user to login on session end.
   * @param ngZone - Runs the idle timers outside Angular so they do not trigger change detection.
   * @param store - NgRx store cleared when the session ends.
   * @param userService - Session identity and token accessor.
   * @param tawkService - Clears the support chat visitor on logout.
   * @param dashboardService - Holds dashboard caches discarded on logout.
   * @param reopeningsFiltersStateManagementService - Reopenings filter state reset on logout.
   * @param managedTabService - Closes tabs this session opened when it ends.
   * @param sessionWatchdog - Stale-session fallback, reset so its deadline cannot fire after an idle logout.
   * @param sessionPolicy - Effective idle timeout (server value, or the 20-minute local default).
   * @param idleWarning - Warning state shown shortly before the idle logout.
   * @param sessionActivity - Client estimate of the server's last-activity clock (drives the timers under a server policy).
   */
  constructor(
    private router: Router,
    private ngZone: NgZone,
    private store: Store,
    private userService: UserService,
    private tawkService: TawkService,
    private dashboardService: DashboardService,
    private reopeningsFiltersStateManagementService: ReopeningsFiltersStateManagementService,
    private managedTabService: ManagedTabService,
    private sessionWatchdog: AuthSessionWatchdogService,
    private sessionPolicy: SessionPolicyService,
    private idleWarning: SessionIdleWarningService,
    private sessionActivity: SessionActivityService
  ) {
    this.startWatching();
    // A newly received server policy (login, renewal, company switch) re-arms the timers with it. The first
    // one also seeds the server-activity estimate (the login response is server activity).
    // N2: a shadow/unknown policy is observation only: it is re-armed (the applied values may have just
    // switched back to the local default) but never seeds the server-activity estimate.
    // D7: a company switch drops the previous tenant's policy (policy$ emits the local default while the session is
    // still live): that must re-arm too, so the old tenant's deadlines and warning do not linger. An emission after
    // the session ended (reset) is not re-armed: arm() would only clear timers anyway.
    this.sessionPolicy.policy$.pipe(skip(1), filter(p => p.source === 'server' || !this.sessionActivity.isEnded)).subscribe((p) => {
      // D5: the seed needs the server's real touch interval; without it (contract C6) nothing is derived.
      if (p.enforcement === 'enforce' && p.touchIntervalMs !== null) {
        const now = Date.now();
        const timing = this.sessionPolicy.lastTiming;
        this.sessionActivity.seedIfUnknown(timing?.sentAtMs ?? now, p.touchIntervalMs, timing?.receivedAtMs ?? now);
      }
      this.arm();
    });
    // D9: a policy read that ended (any outcome) may have left the local-default timer without a deadline decision: re-arm.
    this.sessionPolicy.readPending$.pipe(skip(1), filter(pending => !pending)).subscribe(() => this.arm());
    // Phase 47B Q2: the server-off-suspended state starting or ending changes whether any client-driven logout may run: re-arm.
    this.sessionPolicy.suspended$.pipe(skip(1)).subscribe(() => this.arm());
    // Session ended (this tab's own logout, or a legitimate broadcast logout): nothing may fire any more.
    this.sessionActivity.ended$.subscribe(() => {
      clearTimeout(this.timeout);
      clearTimeout(this.warningTimeout);
      this.idleWarning.setWarning(false);
    });
    // Every server-confirmed activity (counted API call, heartbeat), a sibling tab's activity and the start of a new
    // session (N1: SessionActivityService.startSession) re-arms from the current estimate.
    this.sessionActivity.activity$.subscribe(() => this.arm());
    // A successful "Stay signed in" (already applied to the policy/activity services) re-arms; in local
    // mode it also counts as the user's activity.
    this.idleWarning.staySignedIn$.subscribe(() => {
      this.localActivityAt = Date.now();
      this.sessionActivity.recordLocal(this.localActivityAt, true);
      this.arm();
    });
  }

  /**
   * Computes the warning and logout instants (epoch ms).
   *
   * Server policy in force: both are driven by the SERVER-activity estimate, never by mouse or
   * keyboard. The warning uses the conservative lower bound (warn early); the logout adds the
   * estimate's uncertainty so the client never signs the user out before the server would (a late
   * client logout is harmless: the server answers 401 and the interceptor logs out).
   * No server policy (local default): the pre-existing UI-activity behaviour.
   * @returns The two instants.
   */
  private deadlines(): { warnAt: number; logoutAt: number; absoluteAt: number | null } {
    const enforced = this.sessionPolicy.enforced; // N2: shadow/unknown/missing => local default
    const idleMs = this.sessionPolicy.effectiveIdleMs;
    const warningMs = this.sessionPolicy.warningMs;
    const serverBase = enforced ? this.sessionActivity.lastServerActivityAt : null;
    let warnAt: number;
    let logoutAt: number;
    if (enforced && !this.sessionPolicy.idleDerivable) {
      // D5 / contract C6: enforced but touch_interval_seconds is missing or invalid. Observation only: NO idle logout or
      // warning is derived from activity responses (the server still enforces with 401). The absolute expiry still applies below.
      warnAt = Number.POSITIVE_INFINITY;
      logoutAt = Number.POSITIVE_INFINITY;
    } else if (serverBase !== null) {
      // C2/C3: serverBase is the LOWER bound of the stored last activity (S - T), serverBase + uncertainty its UPPER bound (R).
      // The warning must not be late (lower bound); the logout must not be early (upper bound: R + idle, never S + idle).
      warnAt = serverBase + idleMs - warningMs;
      logoutAt = serverBase + this.sessionActivity.uncertaintyMs + idleMs;
    } else {
      // N1: UI activity seen in any tab counts (shared over the activity channel).
      const localBase = Math.max(this.localActivityAt, this.sessionActivity.lastLocalActivityAt ?? 0);
      warnAt = localBase + idleMs - warningMs;
      logoutAt = localBase + idleMs;
    }
    // N3: the absolute expiry is a hard ceiling under enforce; warn before whichever deadline comes first.
    const abs = this.sessionPolicy.absoluteExpiresAtMs;
    if (abs !== null) {
      warnAt = Math.min(warnAt, abs - warningMs);
      logoutAt = Math.min(logoutAt, abs);
    }
    warnAt = Math.min(warnAt, logoutAt); // C3: the warning is clamped to the logout deadline
    return { warnAt, logoutAt, absoluteAt: abs };
  }

  /**
   * N1: whether this tab has a live session to time out: a session token exists and the activity service is not in the
   * 'ended' state. A logged-out tab never arms a timer, never records activity and never logs out again.
   */
  private isLive(): boolean {
    return !this.sessionActivity.isEnded && this.userService.hasSessionToken();
  }

  /**
   * D9: while a startup / after-switch policy read is in flight and no server policy is enforced yet, the local default
   * (20 min) must not act as a LOGOUT timer: the answer may enforce something else. The read is bounded (10 s timeout) and
   * re-arms through readPending$ when it ends; a failed read leaves the local default in force exactly as before.
   */
  private holdForPolicyRead(): boolean {
    return this.sessionPolicy.readPending && !this.sessionPolicy.enforced;
  }

  /** Clamps a timer delay into the range JS timers support (N3); a clamped timer re-evaluates and re-arms when it fires. */
  private static clampDelay(ms: number): number {
    return Math.min(Math.max(0, ms), MAX_TIMER_DELAY_MS);
  }

  /** (Re)schedules the warning and the idle logout from the current activity estimate. */
  private arm() {
    clearTimeout(this.timeout);
    clearTimeout(this.warningTimeout);
    if (!this.isLive()) {
      // N1: no session (logged out, or never logged in): nothing to time out and no stale warning.
      this.ngZone.run(() => this.idleWarning.setWarning(false));
      return;
    }
    if (this.sessionPolicy.clientLogoutSuspended) {
      // Phase 47B Q2 (server-off-suspended): the server reported Off after an enforced policy. NO client-driven logout: no idle timer,
      // no absolute deadline, no warning, and the 20-minute local default does not act as a logout timer. The server's 401 (the
      // session-status interceptor), an explicit logout and the watchdog are separate paths and are unchanged.
      this.ngZone.run(() => this.idleWarning.setWarning(false));
      return;
    }
    const now = Date.now();
    const { warnAt, logoutAt } = this.deadlines();
    if (!Number.isFinite(logoutAt)) {
      // D5 / C6: enforced without a usable touch interval and no absolute expiry: nothing client-side to time.
      this.ngZone.run(() => this.idleWarning.setWarning(false));
      return;
    }
    if (this.sessionPolicy.warningMs > 0) {
      if (now >= warnAt) {
        this.ngZone.run(() => this.idleWarning.setWarning(true));
      } else {
        // Activity moved the deadline out: the warning no longer applies.
        this.ngZone.run(() => this.idleWarning.setWarning(false));
        this.warningTimeout = setTimeout(() => this.ngZone.run(() => this.arm()), AuthenticationService.clampDelay(warnAt - now));
      }
    } else {
      this.ngZone.run(() => this.idleWarning.setWarning(false));
    }
    this.timeout = setTimeout(() => {
      if (this.sessionPolicy.clientLogoutSuspended) {
        return; // Q2: suspended while this timer was pending (arm() on suspended$ clears it first; this is the belt)
      }
      // N1: re-check against the shared estimate (another tab may have been active since this timer was
      // set); if so re-arm instead of logging out. Applies in both modes. N3: a clamped (very long) timer
      // that fires early also lands in the re-arm branch.
      const d = this.deadlines();
      const t = Date.now();
      if (d.absoluteAt !== null && t >= d.absoluteAt) {
        this.logout(); // hard ceiling: no sibling activity can extend it, so no grace
      } else if (t >= d.logoutAt && this.holdForPolicyRead()) {
        return; // D9: the policy read in flight decides; readPending$ re-arms when it ends
      } else if (t >= d.logoutAt && !this.sessionActivity.hasSiblingChannel) {
        this.logout(); // no sibling channel (unsupported/failed): no in-flight sibling activity to wait for, no grace
      } else if (t >= d.logoutAt) {
        // N1: idle deadline reached. Give in-flight sibling activity a bounded chance to arrive, then re-check.
        // A sibling message in the meantime calls arm(), which replaces this timer.
        this.timeout = setTimeout(() => {
          if (this.sessionPolicy.clientLogoutSuspended) {
            return; // Q2
          }
          if (this.isLive() && Date.now() >= this.deadlines().logoutAt && this.holdForPolicyRead()) {
            return; // D9: see above
          }
          if (this.isLive() && Date.now() >= this.deadlines().logoutAt) {
            this.logout();
          } else {
            this.arm();
          }
        }, IDLE_LOGOUT_GRACE_MS);
      } else {
        this.arm();
      }
    }, AuthenticationService.clampDelay(logoutAt - now));
  }

  private startWatching() {
    this.ngZone.runOutsideAngular(() => {
      const events = ['mousemove', 'keydown', 'mousedown', 'touchstart'];
      events.forEach(event =>
        window.addEventListener(event, () => {
          if (!this.isLive()) {
            return; // N1: a logged-out tab neither records nor shares activity, and never arms a timer
          }
          // With an ENFORCED server policy only server-seen API activity moves the deadline: input alone
          // must neither hide the warning nor postpone the logout the server will enforce.
          // (N2: shadow/unknown/missing keeps the local default below.)
          if (this.sessionPolicy.enforced) {
            // Still remembered (not used, not shared as a deadline) so a later enforce->shadow switch
            // does not find a stale input time and log out at once.
            this.localActivityAt = Date.now();
            return;
          }
          // Local default: while the warning shows, only an explicit "Stay signed in" counts.
          if (!this.idleWarning.isWarning) {
            this.localActivityAt = Date.now();
            this.sessionActivity.recordLocal(this.localActivityAt); // N1: share with the other tabs
            this.arm();
          }
        })
      );
    });
    this.arm();
  }

  /**
  * Log out the current user after the inactivity timeout with the same
  * session-end contract as a manual logout: revokes the session server-side
  * via /token/token-expire-async (fire-and-forget, before local state is
  * cleared), clears all NgRx state, wipes the interceptor-level dedup API
  * cache (clearDedupApiCache via ApiEffects), resets UserService auth state
  * (in-memory token, refresh observable, vaulted credentials), unloads the
  * Tawk chat visitor session, clears localStorage (persisted bearer token)
  * and sessionStorage, and navigates to the email verification screen.
  * @example
  * logout()
  * // void
  * @returns {void} Performs the full inactivity-logout teardown described above.
  **/
  private logout() {
    if (this.sessionActivity.isEnded) {
      return; // N1: idempotent; this session end already ran (a second invocation must not navigate or revoke again)
    }
    this.ngZone.run(() => {
      // The session is ending by another route: make sure the stale-session
      // watchdog cannot fire its own logout on top of this one.
      this.sessionWatchdog.reset();

      // Drop the in-memory token, refresh observable and vaulted LOGIN credentials
      // BEFORE the revocation request is built: with those gone an expired bearer
      // cannot trigger a token-generate call (logout must never mint a token or
      // submit credentials). The vaulted bearer token and the verifier survive this
      // reset, so the revocation below can still be authorised.
      this.userService.resetUserServiceState();

      // Server-side revocation, while the bearer token still exists: hit the
      // same /token/token-expire-async endpoint the manual logout uses so the
      // backend session is invalidated, not just the client. postEmpty builds
      // the request (bearer + X-Payload-HMAC) synchronously on subscription, so
      // the local cleanup below cannot affect it. Fire-and-forget: a failed call
      // must not block the client teardown/navigation. defer() turns a synchronous
      // throw while building the request into an ordinary error handled below.
      defer(() => this.dashboardService.loadTokenExpiration()).pipe(
        // Service-level async failure handling lives in the stream: a failed
        // revocation must not block client teardown/navigation, so swallow it
        // here (after logging) and complete instead of erroring the subscriber.
        catchError((err) => {
          console.error('Idle-logout token revocation failed', err);
          return EMPTY;
        })
      ).subscribe();

      // Clear all Redux state
      this.store.dispatch(clearAllState());

      // Cache clearing is part of the session-end contract: deduped HTTP
      // responses must not survive an inactivity logout and replay after the
      // next login (handled by ApiEffects).
      this.store.dispatch(clearDedupApiCache());

      // An idle timeout must truly end the session: the in-memory token, refresh
      // observable and vaulted credentials were dropped above; now remove the bearer
      // token persisted in localStorage (same contract as performNormalLogout)
      // so guarded routes cannot keep working after the "logout".
      localStorage.clear();
      this.reopeningsFiltersStateManagementService.clearState();

      // Same external-session cleanup as the manual logout path: drop the
      // user-identifying chat visitor state from the Tawk widget.
      this.tawkService.unload();

      // An idle timeout ends the session for every tab the app opened too, so
      // RegAnalytics cannot be left sitting on screen after the user is out.
      this.managedTabService.closeManagedTabs();

      // Clear session storage
      sessionStorage.clear();

      // Navigate to email verification
      this.router.navigate(['/emailVerification']);

      // Session is over: hide the warning, drop the server policy, and tell the other tabs.
      // N1: activity first (state 'ended' + ended$ clears the timers), so the policy reset below cannot re-arm anything.
      this.sessionActivity.reset();
      clearTimeout(this.timeout);
      clearTimeout(this.warningTimeout);
      this.idleWarning.setWarning(false);
      this.sessionPolicy.reset();
      // N1: deliberately NO app-logout broadcast. An idle tab must not sign out a tab whose server session is
      // alive; the other tabs reach the same deadline from the shared estimate, or get the server's 401
      // through the session-status interceptor. D4: the explicit manual logout (non-ADFS, layout performNormalLogout) and the
      // server-ended logouts broadcast; the ADFS manual logout does NOT (it posts no app-logout, as before this work: it leaves
      // for the identity provider and the other tabs reach the server's revocation through their next 401).
    });
  }
}
